// YouTube API Configuration
const YOUTUBE_API_KEY = 'AIzaSyDPyI6vx7_G0amS0Ce-DfaqDplpka9Thq8';

// Playlist IDs
export const PLAYLISTS = {
  WORK: 'PLWkA-VEbp-KrQOPlrmssSiyTy4oXXHPjG',  // Horizontal work videos
  REELS: 'PLWkA-VEbp-Kpwn6eFT8w7MJkhaNti-jFJ', // Vertical reels
  YOUTUBE_EDITS: 'PLWkA-VEbp-KrTHLxtZtl4VRrn4YStICvQ', // YouTube editing work
};

export interface YouTubeVideo {
  id: string;
  videoId: string;
  title: string;
  description: string;
  thumbnail: string;
  thumbnailHigh: string;
  publishedAt: string;
  embedUrl: string;
}

/**
 * Fetch videos from a YouTube playlist
 */
export async function fetchPlaylistVideos(playlistId: string): Promise<YouTubeVideo[]> {
  try {
    const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50&playlistId=${playlistId}&key=${YOUTUBE_API_KEY}`;
    console.log('Fetching playlist:', playlistId);
    
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('YouTube API error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        playlistId
      });
      throw new Error(`YouTube API error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    console.log('YouTube API response:', {
      playlistId,
      itemCount: data.items?.length || 0,
      pageInfo: data.pageInfo
    });

    if (!data.items || data.items.length === 0) {
      console.warn('No items found in playlist:', playlistId);
      return [];
    }

    const videos = data.items
      .filter((item: any) => {
        // Filter out deleted or private videos
        const isValid = item.snippet?.resourceId?.videoId && 
                       item.snippet?.title !== 'Private video' &&
                       item.snippet?.title !== 'Deleted video';
        if (!isValid) {
          console.log('Skipping invalid video:', item.snippet?.title);
        }
        return isValid;
      })
      .map((item: any) => ({
        id: item.id,
        videoId: item.snippet.resourceId.videoId,
        title: item.snippet.title,
        description: item.snippet.description,
        thumbnail: item.snippet.thumbnails.maxres?.url || item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium.url,
        thumbnailHigh: item.snippet.thumbnails.maxres?.url || item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium.url,
        publishedAt: item.snippet.publishedAt,
        embedUrl: `https://www.youtube.com/embed/${item.snippet.resourceId.videoId}?rel=0&modestbranding=1&enablejsapi=1`,
      }));

    console.log('Processed videos:', videos.length);
    return videos;
  } catch (error) {
    console.error('Error fetching YouTube playlist:', error);
    return [];
  }
}

/**
 * Fetch work videos (horizontal format)
 */
export async function fetchWorkVideos(): Promise<YouTubeVideo[]> {
  return fetchPlaylistVideos(PLAYLISTS.WORK);
}

/**
 * Fetch reels videos (vertical format)
 */
export async function fetchReelsVideos(): Promise<YouTubeVideo[]> {
  return fetchPlaylistVideos(PLAYLISTS.REELS);
}

/**
 * Fetch YouTube edits videos
 */
export async function fetchYouTubeEditsVideos(): Promise<YouTubeVideo[]> {
  return fetchPlaylistVideos(PLAYLISTS.YOUTUBE_EDITS);
}

/**
 * Get video embed URL
 */
export function getEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1&enablejsapi=1&origin=${window.location.origin}`;
}

/**
 * Get video thumbnail URL
 */
export function getThumbnailUrl(videoId: string, quality: 'default' | 'medium' | 'high' | 'maxres' = 'high'): string {
  return `https://img.youtube.com/vi/${videoId}/${quality === 'maxres' ? 'maxresdefault' : quality === 'high' ? 'hqdefault' : quality === 'medium' ? 'mqdefault' : 'default'}.jpg`;
}
